
import {
  computed, defineComponent, PropType,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Gleap from 'gleap';
import { buildOperatorPresentation } from '@/composables/flows/analyticsPresentation';
import { formatNumberToCurrency } from '@/helpers/Number';
import { UserState } from '@/composables/User';
import type {
  ContactEvents,
  GlobalStatistics,
  OperatorStepStatus,
  SendStatistics,
} from '@/composables/flows/useFlowAnalytics';

type ContactEventKey = 'received' | 'opened' | 'clicked' | 'ordered';

interface TimelineItem {
  key: ContactEventKey;
  time?: string;
  count?: number;
}

export default defineComponent({
  name: 'FlowAnalyticsChip',

  props: {
    operatorId: {
      type: String,
      required: true,
    },

    /** Type de box (ex. boxsendemail) : détermine la famille de lecture. */
    operatorType: {
      type: String,
      default: '',
    },

    /** Une box en tête de scénario compte des entrées, pas une action attendue. */
    isRoot: {
      type: Boolean,
      default: false,
    },

    /** Événement du déclencheur générique, pour choisir le bon verbe. */
    triggerEvent: {
      type: String,
      default: '',
    },

    globalStats: {
      type: Object as PropType<GlobalStatistics | null>,
      default: null,
    },

    sendStats: {
      type: Object as PropType<SendStatistics | null>,
      default: null,
    },

    /**
     * Mode contact uniquement : ISO 8601 de l'entrée du contact dans cette
     * étape. Sa présence bascule le rendu en mode contact (timeline + badge).
     */
    passedAt: {
      type: String as PropType<string | null>,
      default: null,
    },

    /**
     * Mode contact uniquement : ISO 8601 de la sortie du contact. Utilisé
     * pour les libellés "Bloqué le ..." / "Validé le ..." (instant exact
     * où le worker a tranché pour les boxes filtres).
     */
    exitedAt: {
      type: String as PropType<string | null>,
      default: null,
    },

    /**
     * Mode contact uniquement : statut sémantique du passage du contact.
     * Pilote le libellé/couleur du header + badge.
     */
    stepStatus: {
      type: String as PropType<OperatorStepStatus | null>,
      default: null,
    },

    /**
     * Mode contact + step d'envoi uniquement : événements horodatés du contact.
     */
    contactEvents: {
      type: Object as PropType<ContactEvents | null>,
      default: null,
    },
  },

  setup(props) {
    const { t, locale } = useI18n();

    const hasGlobalStats = computed(() => !!props.globalStats);
    const hasSendStats = computed(() => !!props.sendStats);

    /**
     * Lecture de la box, dérivée des seuls compteurs déjà chargés : aucun appel
     * supplémentaire, et des libellés qui ne dépendent jamais du contenu d'un
     * filtre (voir analyticsPresentation).
     */
    const presentation = computed(() => buildOperatorPresentation({
      operatorType: props.operatorType,
      isRoot: props.isRoot,
      event: props.triggerEvent || undefined,
      global: props.globalStats,
      send: props.sendStats,
    }));

    /**
     * Vrai sur la première ligne d'un groupe autre que le précédent : sert à
     * poser un filet entre « n'ont pas reçu » et « après réception ».
     */
    const isGroupStart = (index: number): boolean => {
      const lines = presentation.value.breakdown;
      return index > 0 && lines[index].group !== lines[index - 1].group;
    };

    /**
     * Colonnes de l'entonnoir : une par étape, mais deux rangées de deux au-delà
     * de trois, pour ne pas comprimer les libellés sur la largeur d'une box.
     */
    const funnelStyle = computed(() => {
      const count = presentation.value.funnel.length;
      const columns = count > 3 ? 2 : Math.max(count, 1);
      return { gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` };
    });

    /**
     * Bloc revenus : masqué tant qu'aucune commande n'est attribuée. Le montant
     * arrive déjà formaté par l'API avec la devise de la boutique.
     */
    const revenue = computed(() => {
      const value = presentation.value.revenue;
      if (!value || (!value.orders && !value.amount)) {
        return null;
      }
      // L'éditeur reçoit un montant déjà mis en forme avec la devise ; l'API
      // publique renvoie un nombre brut, qu'on met en forme ici.
      const { amount } = value;
      const label = typeof amount === 'number'
        ? formatNumberToCurrency(amount, UserState.activeShop?.currency ?? 'EUR')
        : amount;
      return { orders: value.orders, amount: label };
    });

    /**
     * Segments de la barre de répartition : ce qui a continué, puis chaque motif
     * d'arrêt. La somme vaut toujours le total arrivé sur la box.
     */
    const splitSegments = computed(() => {
      const {
        family, headline, breakdown, splitBase,
      } = presentation.value;
      const first = splitBase ?? headline?.value ?? 0;
      // Une attente en cours n'est pas un aboutissement : la teinter en vert la
      // rendrait indiscernable de la ligne « ont terminé l’attente ».
      const firstTone = family === 'wait' ? 'wait' : 'go';
      return [
        { tone: firstTone, value: first },
        ...breakdown
          .filter((item) => item.group !== 'after')
          .map((item) => ({ tone: item.tone, value: item.value })),
      ].filter((segment) => segment.value > 0);
    });

    /** Taux affiché avec le séparateur décimal de la langue active. */
    const formatRate = (value: number): string => {
      const rounded = value < 1 && value > 0 ? value.toFixed(1) : String(Math.round(value));
      return `${rounded.replace('.', locale.value.startsWith('fr') ? ',' : '.')} %`;
    };

    /**
     * Nombre mis en forme dans la langue de l'interface, et non dans celle du
     * navigateur : un compte en français sur un navigateur anglais lisait
     * « 4,120 » au milieu de libellés français.
     */
    const formatValue = (value: number | string): string => {
      if (typeof value === 'string') return value;
      return value.toLocaleString(locale.value);
    };

    // ── Mode contact ───────────────────────────────────────────────────────
    const isContactMode = computed(() => !!props.passedAt);

    const hasContactTimeline = computed(
      () => !!props.contactEvents
        && Object.values(props.contactEvents).some((v) => !!v),
    );

    const timelineItems = computed<TimelineItem[]>(() => {
      const evts = props.contactEvents ?? {};
      return [
        { key: 'received', time: evts.receivedAt },
        { key: 'opened', time: evts.openedAt, count: evts.openCount },
        { key: 'clicked', time: evts.clickedAt, count: evts.clickCount },
        { key: 'ordered', time: evts.orderedAt, count: evts.orderCount },
      ];
    });

    // Locale i18n active (fr / en / ...) — utilisée pour formater date et
    // heure selon la langue du user : 24h naturellement en français, AM/PM en
    // anglais, etc. Aucun replace hardcodé.
    const formatDate = (iso?: string | null): string => {
      if (!iso) return '';
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return '';
      return d.toLocaleDateString(locale.value, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    };

    const formatTime = (iso?: string | null): string => {
      if (!iso) return '';
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return '';
      return d.toLocaleTimeString(locale.value, {
        hour: '2-digit',
        minute: '2-digit',
      });
    };

    /**
     * Format compact « DD/MM HH:MM » pour la timeline événements — peut
     * couvrir des jours différents que le passedAt (ouverture/clic peuvent
     * arriver plusieurs jours après l'envoi).
     */
    const formatDateTime = (iso?: string | null): string => {
      if (!iso) return '';
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return '';
      const date = d.toLocaleDateString(locale.value, {
        day: '2-digit',
        month: '2-digit',
      });
      const time = d.toLocaleTimeString(locale.value, {
        hour: '2-digit',
        minute: '2-digit',
      });
      return `${date} ${time}`;
    };

    // Pour le header + badge en mode contact on préfère afficher
    // l'instant de sortie (exitedAt) plutôt que l'entrée — c'est lui qui
    // est sémantiquement pertinent pour "Bloqué le …" / "Validé le …".
    // Fallback sur passedAt si exitedAt n'est pas fourni.
    const chipReferenceDate = computed(
      () => props.exitedAt ?? props.passedAt,
    );
    const formattedPassedAtDate = computed(() => formatDate(chipReferenceDate.value));
    const formattedPassedAtTime = computed(() => formatTime(chipReferenceDate.value));

    // Clé i18n du header line. Suffixée par stepStatus quand disponible
    // (passed/stopped/finished/paused/blockedByMarketingPressure/
    // blockNoCreditsSms), fallback `default` (= ancien libellé "Passé le …").
    const headerLineI18nKey = computed(() => {
      const status = props.stepStatus;
      return status
        ? `workflowStats.chip.headerLine.${status}`
        : 'workflowStats.chip.headerLine.default';
    });

    // Idem pour le badge final ("Étape franchie" / "Filtre non validé" / …).
    const badgeI18nKey = computed(() => {
      const status = props.stepStatus;
      return status
        ? `workflowStats.chip.badge.${status}`
        : 'workflowStats.chip.badge.default';
    });

    // Mapping stepStatus → variante visuelle CSS (success/danger/warning/info).
    // Sert à colorer le badge ET le header line de manière cohérente.
    const badgeVariant = computed<'success' | 'danger' | 'warning' | 'info'>(() => {
      switch (props.stepStatus) {
        case 'stopped':
          return 'danger';
        case 'paused':
          return 'warning';
        case 'finished':
          return 'info';
        case 'blockedByMarketingPressure':
        case 'blockNoCreditsSms':
        case 'blockedByPlanLimit':
        case 'campaignInactive':
        case 'blockedByDeliverability':
        case 'blockedByPersistence':
        case 'blockedByMaxDelay':
        case 'outOfDateRange':
        case 'campaignDisabled':
          return 'warning';
        // Un panier déjà traité est une fin normale, pas un incident.
        case 'cartAlreadyProcessed':
          return 'info';
        // Cause non enregistrée par le moteur : seul le support peut trancher.
        case 'internalError':
          return 'danger';
        case 'passed':
        default:
          return 'success';
      }
    });

    // Icône Font Awesome dérivée du status — cohérente avec le badge.
    const badgeIcon = computed(() => {
      switch (props.stepStatus) {
        case 'stopped':
          return 'fa-solid fa-circle-xmark';
        case 'paused':
          return 'fa-solid fa-hourglass-half';
        case 'finished':
          return 'fa-solid fa-flag-checkered';
        case 'blockedByMarketingPressure':
        case 'blockNoCreditsSms':
        case 'blockedByPlanLimit':
        case 'campaignInactive':
        case 'blockedByDeliverability':
        case 'blockedByPersistence':
        case 'outOfDateRange':
        case 'campaignDisabled':
          return 'fa-solid fa-ban';
        case 'blockedByMaxDelay':
          return 'fa-solid fa-hourglass-end';
        case 'cartAlreadyProcessed':
          return 'fa-solid fa-cart-shopping';
        case 'internalError':
          return 'fa-solid fa-circle-question';
        case 'passed':
        default:
          return 'fa-solid fa-check';
      }
    });

    /**
     * Seule la cause inconnue mène au support : le marchand n'a rien à corriger
     * lui-même, et le code interne du moteur ne lui dirait rien.
     */
    const opensSupport = computed(() => props.stepStatus === 'internalError');

    /** Ouvre le widget d'assistance, avec repli sur un courriel s'il est absent. */
    const openSupport = () => {
      try {
        Gleap.open();
      } catch {
        window.open('mailto:contact@shopimind.com', '_blank');
      }
    };

    return {
      t,
      hasGlobalStats,
      hasSendStats,
      presentation,
      isGroupStart,
      funnelStyle,
      revenue,
      splitSegments,
      formatRate,
      formatValue,
      isContactMode,
      hasContactTimeline,
      timelineItems,
      headerLineI18nKey,
      badgeI18nKey,
      badgeVariant,
      badgeIcon,
      opensSupport,
      openSupport,
      formattedPassedAtDate,
      formattedPassedAtTime,
      formatDateTime,
    };
  },
});
